import {addDays} from 'date-fns/addDays'
import {SearchLocationType} from 'hooks/searchBox'
import {SearchUrlParams} from 'modules/search/types'
import QueryString from 'qs'
import {pipe, props} from 'ramda'
import {isEmpty} from 'ramda'
import {Except} from 'type-fest'
import {convertHotelSlugToHotelId} from 'views/accommodation/convertHotelSlugToHotelId'

import {
  dateStringToMiddayDate,
  dateToMiddayString
} from '@vio/core/src/utils/date'
import {parseQueryString} from '@vio/core/src/utils/url'

import type {ApiSearchParameters, BoundingBox} from '@findhotel/sapi'

type ValidSearchParams = Pick<
  SearchUrlParams,
  | 'placeId'
  | 'hotelId'
  | 'boundingBox'
  | 'userLocationSearch'
  | 'address'
  | 'iataCode'
>

/**
 * Validates if URL search parameters have all required parameters
 * @param searchParams
 */
export const isValidSearchUrlParams = (searchParams: ValidSearchParams) => {
  return Boolean(
    searchParams.placeId ??
      searchParams.hotelId ??
      searchParams.boundingBox ??
      searchParams.userLocationSearch ??
      searchParams.address ??
      searchParams.iataCode
  )
}

/**
 * Validates if SAPI search parameters have all required parameters
 */
export const isValidSapiSearchParameters = (
  searchParameters: ApiSearchParameters
) =>
  !isEmpty(searchParameters) &&
  (searchParameters.hotelId !== undefined ||
    searchParameters.placeId !== undefined ||
    searchParameters.boundingBox !== undefined ||
    searchParameters.address !== undefined ||
    searchParameters.geolocation !== undefined ||
    searchParameters.iataCode !== undefined)

interface DestinationIds {
  hotelId?: string
  placeId?: string
}

/**
 * Get the destination ids from the search params
 * @param params
 */
export const getDestinationIds: (params: DestinationIds) => string[] = props([
  'hotelId',
  'placeId'
])

/**
 * Get the destination parameter in the correct format
 * @param type
 * @param value
 */
export const getDestinationParameter = (
  type: Omit<SearchLocationType, 'userLocationSearch'>,
  value: string | null | undefined | BoundingBox
): Record<string, unknown> => {
  switch (type) {
    case 'hotel': {
      return {hotelId: value}
    }

    case 'place': {
      return {placeId: value}
    }

    case 'address': {
      return {address: value}
    }

    case 'area': {
      return {boundingBox: value}
    }

    default: {
      return {}
    }
  }
}

type SerializedUrlParamsWithoutTempParams = Except<
  Record<string, QueryString.ParsedQs>,
  | 'scrollAnchorId'
  | 'anonymousId'
  | 'openHotelDetails'
  | 'scrollToRooms'
  | 'scrollToRoom'
  | 'showAllOffers'
>

const multiValueFilters = [
  'facilities',
  'themes',
  'propertyTypes',
  'amenities',
  'starRatings',
  'boundingBox',
  'neighborhoodIds'
]
const convertMultiValueFiltersToArr = obj => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key] =
      multiValueFilters.includes(key) && !Array.isArray(obj[key])
        ? [obj[key]]
        : obj[key]
    return acc
  }, {})
}

const removeDuplicateParams = (obj: Record<string, unknown>) => {
  return Object.keys(obj).reduce((acc, key) => {
    if (Array.isArray(obj[key]) && !multiValueFilters.includes(key)) {
      acc[key] = obj[key][0]
    } else {
      acc[key] = obj[key]
    }
    return acc
  }, {})
}

/**
 * Gets the current url params as a parsed object with some processing for multi-value filters and duplicate params.
 * @param queryString - The query string to serialize
 * @returns The serialized URL params
 */
export const getSerializedUrlParams = (
  queryString?: string
): SerializedUrlParamsWithoutTempParams =>
  pipe(
    parseQueryString,
    convertMultiValueFiltersToArr,
    removeDuplicateParams
  )(queryString)

/**
 * Replace the value of the `checkOut` param by extending it to match
 * the number of days from the `mlos` param.
 * @param urlParams - The URL params with or without `mlos`
 * @returns The URL params with the new value for `checkOut` if the `mlos` param exists
 */
export const replaceCheckOutWithMlos = (
  urlParams: SearchUrlParams
): SearchUrlParams => {
  const {mlos, checkIn, ...rest} = urlParams
  const shouldUseMlos = mlos && checkIn

  if (!shouldUseMlos) {
    return {
      ...rest,
      checkIn
    }
  }

  const mlosCheckOut = pipe(
    checkIn => dateStringToMiddayDate(checkIn),
    checkIn => addDays(checkIn, Number(mlos)),
    checkIn => dateToMiddayString(checkIn)
  )(checkIn)

  return {
    ...urlParams,
    checkOut: mlosCheckOut
  }
}

// Append path parameters to the query string
// e.g., /p/12345/en/some-taxonomy -> ?placeId=12345
// e.g., /Hotel/1234 -> ?hotelSlug=1234
export const appendPathParamsToSearchParams = (
  searchParams: URLSearchParams,
  pathParams: Record<string, string | string[] | undefined> = {}
) => {
  if (pathParams.placeId)
    searchParams.set('placeId', pathParams.placeId as string)

  const hotelId = convertHotelSlugToHotelId(pathParams.hotelSlug as string)
  if (hotelId) {
    searchParams.set('hotelSlug', pathParams.hotelSlug as string)
    searchParams.set('hotelId', hotelId)
  }
  if (pathParams.bookingId)
    searchParams.set('bookingId', pathParams.bookingId as string)

  return {
    queryParams: searchParams,
    queryString: searchParams.toString()
  }
}
